var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { UNIT, SHAPE_BOUNDS } from './patternShapesConstants';
import { computeGroupSnapDelta } from './patternShapesSnapHelper';
import { calculateBoxWithPaddingAndRotation, bringManipulativesToFront } from '../helper/helper';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { playAudio } from '../helper/audioHelper';
import customEventHandler from '../helper/customEvents';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
import { pendingDuplicateIds } from '../helper/hooks/useDuplicateAnnounce';
var PADDING = 12;
var HANDLE_SIZE = 40;
var HANDLE_GAP = 20;
var CIRCLE_SIZE = 12;
var PatternShapesSelectionGroup = function (_a) {
    var selectedShapeIds = _a.selectedShapeIds, children = _a.children, groupId = _a.groupId;
    var _b = useContext(DataContext), manipulative = _b.manipulative, setManipulative = _b.setManipulative, selectedTool = _b.selectedTool, setSelectedTool = _b.setSelectedTool, setContextMenu = _b.setContextMenu;
    var _c = globalStore.useStoreState(function (s) { return s.playerStore; }), zoomLevel = _c.zoomLevel, playerBgPattern = _c.playerBgPattern;
    var t = useTranslation().t;
    // For permanent groups: active when the group ID is in selectedTool.
    // For transient multi-select: always active (component only renders when selected).
    var isGroupActive = groupId ? selectedTool.includes(groupId) : true;
    // Broadcasts this overlay's drag as a multi-select leader so OTHER-type items (dice,
    // tiles, NMP groups, ...) selected alongside it also follow — startGroupDrag below
    // only ever moved this overlay's OWN selectedShapeIds directly via setManipulative,
    // never telling the shared multi-select bus a drag was happening, so any other-type
    // follower never moved when a pattern-shapes group/multi-select led the drag. The
    // leader id matches the `data-manipulative-id` set on the drag-area div below.
    var _d = useMultiSelectDragSource(groupId !== null && groupId !== void 0 ? groupId : selectedShapeIds[0], selectedTool), startMultiDrag = _d.startMultiDrag, moveMultiDrag = _d.moveMultiDrag, endMultiDrag = _d.endMultiDrag;
    /* ── Refs for drag ── */
    var dragStartRef = useRef(null);
    var shapeCentersAtDragStart = useRef({});
    var hasMoved = useRef(false);
    /* ── Refs for rotate ── */
    var rotatingRef = useRef(false);
    var rotateHasMovedRef = useRef(false);
    var groupCenterScreenRef = useRef({ x: 0, y: 0 });
    var groupCenterCanvasRef = useRef({ x: 0, y: 0 });
    var rotateStartAngleRef = useRef(0);
    var initialCentersRef = useRef({});
    var initialRotationsRef = useRef({});
    var initialScalesRef = useRef({});
    var _e = useState(false), rotateHandleHover = _e[0], setRotateHandleHover = _e[1];
    /* ── Persistent overlay state ── */
    var _f = useState(0), groupAngle = _f[0], setGroupAngle = _f[1];
    var _g = useState(0), rotDelta = _g[0], setRotDelta = _g[1];
    var rotDeltaRef = useRef(0);
    var manipulativeRef = useRef(manipulative);
    manipulativeRef.current = manipulative;
    var groupAngleRef = useRef(0);
    groupAngleRef.current = groupAngle;
    var baseBoundsRef = useRef(null);
    var baseCirclesRef = useRef([]);
    // Initialize to the actual computed key so the selectionKey useEffect doesn't fire on
    // first mount and clear baseBoundsRef before startGroupDrag can capture boundsSnap.
    var selectionKeyRef = useRef(selectedShapeIds.slice().sort().join(','));
    var scaleKeyRef = useRef('');
    var centerKeyRef = useRef('');
    var _h = useState(false), isDragging = _h[0], setIsDragging = _h[1];
    /* ── Focus drag area when this group is newly created via context menu ── */
    useEffect(function () {
        if (!groupId)
            return;
        var handler = function (data) {
            var _a;
            if (data.groupId !== groupId)
                return;
            (_a = dragAreaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        };
        customEventHandler.addEventListener('focusPsGroup', handler);
        return function () { return customEventHandler.removeEventListener('focusPsGroup', handler); };
    }, [groupId]);
    /* ── Duplicate label override — temporarily replaces aria-label on focus (same pattern as individual PS) ── */
    var _j = useState(function () {
        if (groupId && pendingDuplicateIds.has(groupId)) {
            pendingDuplicateIds.delete(groupId);
            return t('text.duplicatedAnnounce', { name: t('text.patternBlockGroup') });
        }
        return null;
    }), duplicateOverrideLabel = _j[0], setDuplicateOverrideLabel = _j[1];
    useEffect(function () {
        if (!duplicateOverrideLabel)
            return;
        var timer = setTimeout(function () { return setDuplicateOverrideLabel(null); }, 500);
        return function () { return clearTimeout(timer); };
    }, [duplicateOverrideLabel]);
    /* ── Auto-focus when group becomes selected (e.g. after duplication) ── */
    var wasGroupActiveRef = useRef(false);
    useEffect(function () {
        if (!groupId)
            return;
        var wasActive = wasGroupActiveRef.current;
        wasGroupActiveRef.current = isGroupActive;
        if (isGroupActive && !wasActive) {
            setTimeout(function () {
                var _a, _b;
                if ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))
                    return;
                (_b = dragAreaRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }, 100);
        }
    }, [isGroupActive, groupId]);
    /* ── Keyboard movement refs + announce state ── */
    var keyMoveSaveTimer = useRef(undefined);
    var keyMoveStartRef = useRef(null);
    var _k = useState(''), announceText = _k[0], setAnnounceText = _k[1];
    var dragAreaRef = useRef(null);
    var lastFocusWasScreenReader = useRef(false);
    var rotateProxyRef = useRef(null);
    var rotateProxyFocusedRef = useRef(false);
    var rotateHandleVisualRef = useRef(null);
    /* ── Rotate helper — used by context menu events and keyboard handle ── */
    var rotateGroup = function (degDelta) {
        var b = baseBoundsRef.current;
        if (!b)
            return;
        var gcx = b.centerX;
        var gcy = b.centerY;
        var rad = (degDelta * Math.PI) / 180;
        var cos = Math.cos(rad);
        var sin = Math.sin(rad);
        var currentShapes = manipulativeRef.current.filter(function (m) { var _a; return selectedShapeIds.includes((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); });
        if (currentShapes.length === 0)
            return;
        // Compute rotated positions first so we can snap them
        var rotatedShapes = currentShapes.map(function (item) {
            var _a, _b;
            var d = item.data;
            var sb = SHAPE_BOUNDS[d.shapeType] || { x: 0, y: 0, w: UNIT, h: UNIT };
            var halfW = sb.w / 2;
            var halfH = sb.h / 2;
            var cx = d.center.x + halfW;
            var cy = d.center.y + halfH;
            var newCx = gcx + (cx - gcx) * cos - (cy - gcy) * sin;
            var newCy = gcy + (cx - gcx) * sin + (cy - gcy) * cos;
            return {
                id: item.data.id,
                shapeType: d.shapeType,
                center: { x: newCx - halfW, y: newCy - halfH },
                rotation: ((_a = d.rotation) !== null && _a !== void 0 ? _a : 0) + degDelta,
                scale: (_b = d.scale) !== null && _b !== void 0 ? _b : 1,
            };
        });
        // Snap the rotated group to nearby shapes
        var excludeIds = groupId ? __spreadArray(__spreadArray([], selectedShapeIds, true), [groupId], false) : selectedShapeIds;
        var _a = computeGroupSnapDelta(rotatedShapes, excludeIds, manipulativeRef.current, playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none'), snapDx = _a.dx, snapDy = _a.dy;
        if (snapDx !== 0 || snapDy !== 0)
            playAudio('PatternAdd');
        var ids = new Set(selectedShapeIds);
        var rotatedMap = new Map(rotatedShapes.map(function (s) { return [s.id, s]; }));
        setManipulative(function (prev) {
            return prev.map(function (item) {
                var _a;
                if (!ids.has((_a = item.data.id) !== null && _a !== void 0 ? _a : ''))
                    return item;
                var r = rotatedMap.get(item.data.id);
                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: r.center.x + snapDx, y: r.center.y + snapDy }, rotation: r.rotation }) });
            });
        });
        baseBoundsRef.current = null;
        baseCirclesRef.current = [];
        setGroupAngle(function (prev) { return prev + degDelta; });
        var newBaseline = (((groupAngleRef.current + degDelta) % 360) + 360) % 360;
        setAnnounceText('');
        var didGroupSnap = snapDx !== 0 || snapDy !== 0;
        requestAnimationFrame(function () {
            rotatingRef.current = false;
            var rotMsg = t('text.patternShapeRotated', {
                degrees: Math.abs(degDelta),
                direction: degDelta > 0 ? t('text.clockwise') : t('text.anticlockwise'),
                baseline: Math.round(newBaseline),
            });
            if (didGroupSnap) {
                setAnnounceText(rotMsg + ' ' + t('text.patternShapeSnappedAt', {
                    name: t('text.patternBlockGroup'),
                    x: Math.round(gcx + snapDx),
                    y: Math.round(gcy + snapDy),
                }));
            }
            else {
                setAnnounceText(rotMsg);
            }
        });
        rotatedShapes.forEach(function (r) {
            var _a;
            var orig = currentShapes.find(function (s) { return s.data.id === r.id; });
            var d = orig === null || orig === void 0 ? void 0 : orig.data;
            saveState({
                type: r.id, id: r.id.split('_')[1], propertyName: 'completeData',
                old: JSON.stringify({ center: d.center, rotation: (_a = d.rotation) !== null && _a !== void 0 ? _a : 0 }),
                new: JSON.stringify({ center: { x: r.center.x + snapDx, y: r.center.y + snapDy }, rotation: r.rotation }),
            });
        });
    };
    /* ── Context menu actions (rotate, scale, duplicate) for the whole group ── */
    useEffect(function () {
        var handler = function (data) {
            var _a;
            if (!isGroupActive)
                return;
            if (data.id === 'rotateClockwise' || data.id === 'rotateAnticlockwise') {
                var degDelta = data.id === 'rotateClockwise' ? 15 : -15;
                rotateGroup(degDelta);
                return;
            }
            if (data.id === 'scalePlus' || data.id === 'scaleMinus') {
                var first = manipulativeRef.current.find(function (m) { var _a; return selectedShapeIds.includes((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); });
                if (!first)
                    return;
                var oldScale = (_a = first.data.scale) !== null && _a !== void 0 ? _a : 1;
                var newScale_1 = data.id === 'scalePlus'
                    ? Math.min(5, Math.round((oldScale + 0.1) * 10) / 10)
                    : Math.max(0.5, Math.round((oldScale - 0.1) * 10) / 10);
                setAnnounceText('');
                requestAnimationFrame(function () {
                    return setAnnounceText(t('text.patternShapeScaled', {
                        direction: data.id === 'scalePlus' ? t('text.up') : t('text.down'),
                        value: newScale_1,
                    }));
                });
                return;
            }
        };
        customEventHandler.addEventListener('bottomPanel', handler);
        return function () { return customEventHandler.removeEventListener('bottomPanel', handler); };
    }, [selectedShapeIds, setManipulative, isGroupActive]);
    /* ── Get selected shapes ── */
    var selectedShapes = useMemo(function () {
        return manipulative.filter(function (m) { var _a; return selectedShapeIds.includes((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); });
    }, [manipulative, selectedShapeIds]);
    /* ── Compute live axis-aligned bounds ── */
    var liveBounds = useMemo(function () {
        if (selectedShapes.length === 0)
            return null;
        var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        selectedShapes.forEach(function (item) {
            var _a, _b;
            var center = item.data.center;
            var scale = (_a = item.data.scale) !== null && _a !== void 0 ? _a : 1;
            var rotation = (_b = item.data.rotation) !== null && _b !== void 0 ? _b : 0;
            var shapeType = item.data.shapeType;
            var bounds = SHAPE_BOUNDS[shapeType] || { x: 0, y: 0, w: UNIT, h: UNIT };
            var vcx = center.x + bounds.w / 2;
            var vcy = center.y + bounds.h / 2;
            var w = bounds.w * scale;
            var h = bounds.h * scale;
            var box = calculateBoxWithPaddingAndRotation(vcx, vcy, w, h, rotation, 0, true);
            minX = Math.min(minX, box.minX);
            minY = Math.min(minY, box.minY);
            maxX = Math.max(maxX, box.maxX);
            maxY = Math.max(maxY, box.maxY);
        });
        return {
            minX: minX - PADDING, minY: minY - PADDING,
            maxX: maxX + PADDING, maxY: maxY + PADDING,
            width: maxX - minX + PADDING * 2, height: maxY - minY + PADDING * 2,
            centerX: (minX + maxX) / 2, centerY: (minY + maxY) / 2,
        };
    }, [selectedShapes]);
    /* ── Compute live drag circles ── */
    var liveCircles = useMemo(function () {
        return selectedShapes.map(function (item) {
            var center = item.data.center;
            var shapeType = item.data.shapeType;
            var bounds = SHAPE_BOUNDS[shapeType] || { x: 0, y: 0, w: UNIT, h: UNIT };
            return { id: item.data.id, cx: center.x + bounds.w / 2, cy: center.y + bounds.h / 2 };
        });
    }, [selectedShapes]);
    /* ── Reset base bounds when selection changes ── */
    var selectionKey = selectedShapeIds.slice().sort().join(',');
    // Guarantees a re-render after nulling the base bounds/circles refs below,
    // even when groupAngle/rotDelta are already 0 (the common case for a
    // freshly duplicated selection) — setGroupAngle(0)/setRotDelta(0) would
    // otherwise be no-ops (React bails out on an unchanged state value), so
    // no render would occur to run the "populate if null" logic further down
    // before the user's next mousedown. Without that repopulation, a group
    // drag starting right after a duplicate captures a null bounds snapshot
    // and its overlay (bounding box + rotate handle) freezes for the whole
    // drag, only catching up to the real position on drop.
    var _l = useState(0), forceBoundsRepopulate = _l[1];
    useEffect(function () {
        if (selectionKey !== selectionKeyRef.current) {
            selectionKeyRef.current = selectionKey;
            baseBoundsRef.current = null;
            baseCirclesRef.current = [];
            setGroupAngle(0);
            setRotDelta(0);
            rotDeltaRef.current = 0;
            forceBoundsRepopulate(function (t) { return t + 1; });
        }
    }, [selectionKey]);
    var scaleKey = selectedShapes.map(function (m) { return "".concat(m.data.id, ":").concat(m.data.scale); }).join(',');
    if (scaleKey !== scaleKeyRef.current) {
        scaleKeyRef.current = scaleKey;
        baseBoundsRef.current = null;
        baseCirclesRef.current = [];
    }
    var centerKey = selectedShapes
        .map(function (m) { var c = m.data.center; return "".concat(m.data.id, ":").concat(Math.round(c.x), ",").concat(Math.round(c.y)); })
        .join('|');
    if (centerKey !== centerKeyRef.current) {
        centerKeyRef.current = centerKey;
        if (!isDragging && !rotatingRef.current && baseBoundsRef.current) {
            baseBoundsRef.current = null;
            baseCirclesRef.current = [];
        }
    }
    if (!baseBoundsRef.current && liveBounds) {
        // liveCircles / liveShapes are at committed canvas positions. The overlay applies
        // rotate(totalAngle) on top, so both baseCirclesRef and baseBoundsRef must be stored
        // in the inverse-rotated (pre-rotation) space. Without this, any accumulated groupAngle
        // (e.g. from context-menu rotations before a scale) would double-rotate after scale
        // clears and repopulates the refs.
        // Pivot is the centroid of shape visual centers (matches the displayed marker), so the
        // rotation pivot, inverse-rotation pivot, and CSS transform-origin all agree.
        var liveCentroid = liveCircles.length > 0
            ? {
                x: liveCircles.reduce(function (s, c) { return s + c.cx; }, 0) / liveCircles.length,
                y: liveCircles.reduce(function (s, c) { return s + c.cy; }, 0) / liveCircles.length,
            }
            : { x: liveBounds.centerX, y: liveBounds.centerY };
        var currentAngle = groupAngle + rotDelta;
        if (currentAngle !== 0) {
            var rad = (-currentAngle * Math.PI) / 180;
            var icos_1 = Math.cos(rad);
            var isin_1 = Math.sin(rad);
            var ocx_1 = liveCentroid.x;
            var ocy_1 = liveCentroid.y;
            var irCircles_1 = liveCircles.map(function (c) { return (__assign(__assign({}, c), { cx: ocx_1 + (c.cx - ocx_1) * icos_1 - (c.cy - ocy_1) * isin_1, cy: ocy_1 + (c.cx - ocx_1) * isin_1 + (c.cy - ocy_1) * icos_1 })); });
            baseCirclesRef.current = irCircles_1;
            // Compute baseBounds from inverse-rotated shape positions so that
            // overlay's rotate(totalAngle) maps it back to the correct visual extent.
            var bMinX_1 = Infinity, bMinY_1 = Infinity, bMaxX_1 = -Infinity, bMaxY_1 = -Infinity;
            selectedShapes.forEach(function (item, idx) {
                var _a;
                var irC = irCircles_1[idx];
                if (!irC)
                    return;
                var scale = (_a = item.data.scale) !== null && _a !== void 0 ? _a : 1;
                var st = item.data.shapeType;
                var sb = SHAPE_BOUNDS[st] || { x: 0, y: 0, w: UNIT, h: UNIT };
                var hw = (sb.w * scale) / 2;
                var hh = (sb.h * scale) / 2;
                bMinX_1 = Math.min(bMinX_1, irC.cx - hw);
                bMinY_1 = Math.min(bMinY_1, irC.cy - hh);
                bMaxX_1 = Math.max(bMaxX_1, irC.cx + hw);
                bMaxY_1 = Math.max(bMaxY_1, irC.cy + hh);
            });
            baseBoundsRef.current = {
                minX: bMinX_1 - PADDING, minY: bMinY_1 - PADDING,
                maxX: bMaxX_1 + PADDING, maxY: bMaxY_1 + PADDING,
                width: bMaxX_1 - bMinX_1 + PADDING * 2, height: bMaxY_1 - bMinY_1 + PADDING * 2,
                centerX: ocx_1, centerY: ocy_1,
            };
        }
        else {
            baseBoundsRef.current = __assign(__assign({}, liveBounds), { centerX: liveCentroid.x, centerY: liveCentroid.y });
            baseCirclesRef.current = liveCircles.map(function (c) { return (__assign({}, c)); });
        }
    }
    /* ================================================================ */
    /* GROUP DRAG                                                        */
    /* ================================================================ */
    var startGroupDrag = function (clientX, clientY) {
        dragStartRef.current = { x: clientX, y: clientY };
        hasMoved.current = false;
        shapeCentersAtDragStart.current = {};
        selectedShapes.forEach(function (item) {
            var _a;
            var iid = (_a = item.data.id) !== null && _a !== void 0 ? _a : '';
            shapeCentersAtDragStart.current[iid] = {
                x: item.data.center.x,
                y: item.data.center.y,
            };
        });
        var boundsSnap = baseBoundsRef.current ? __assign({}, baseBoundsRef.current) : null;
        var circlesSnap = baseCirclesRef.current.map(function (c) { return (__assign({}, c)); });
        document.body.style.cursor = 'grabbing';
        var onMove = function (e) {
            if (!dragStartRef.current)
                return;
            var cx = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var cy = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var dx = (cx - dragStartRef.current.x) / zoomLevel;
            var dy = (cy - dragStartRef.current.y) / zoomLevel;
            if (Math.hypot(dx * zoomLevel, dy * zoomLevel) > 4) {
                if (!hasMoved.current) {
                    hasMoved.current = true;
                    setIsDragging(true);
                    startMultiDrag();
                }
            }
            if (!hasMoved.current)
                return;
            // Multi-select: broadcast delta so OTHER-type items follow
            moveMultiDrag(dx, dy);
            if (boundsSnap) {
                baseBoundsRef.current = __assign(__assign({}, boundsSnap), { minX: boundsSnap.minX + dx, minY: boundsSnap.minY + dy, maxX: boundsSnap.maxX + dx, maxY: boundsSnap.maxY + dy, centerX: boundsSnap.centerX + dx, centerY: boundsSnap.centerY + dy });
            }
            baseCirclesRef.current = circlesSnap.map(function (c) { return (__assign(__assign({}, c), { cx: c.cx + dx, cy: c.cy + dy })); });
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a;
                    var startCenter = shapeCentersAtDragStart.current[(_a = item.data.id) !== null && _a !== void 0 ? _a : ''];
                    if (!startCenter)
                        return item;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: startCenter.x + dx, y: startCenter.y + dy } }) });
                });
            });
        };
        var onUp = function () {
            var _a, _b;
            if (hasMoved.current) {
                var currentManipulative_1 = manipulativeRef.current;
                var snapShapes = selectedShapeIds.flatMap(function (sid) {
                    var _a, _b;
                    var item = currentManipulative_1.find(function (m) { return m.data.id === sid; });
                    if (!item)
                        return [];
                    var d = item.data;
                    return [{ id: sid, shapeType: d.shapeType, center: d.center, rotation: (_a = d.rotation) !== null && _a !== void 0 ? _a : 0, scale: (_b = d.scale) !== null && _b !== void 0 ? _b : 1 }];
                });
                var excludeIds = groupId ? __spreadArray(__spreadArray([], selectedShapeIds, true), [groupId], false) : selectedShapeIds;
                var _c = computeGroupSnapDelta(snapShapes, excludeIds, currentManipulative_1, playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none'), snapDx_1 = _c.dx, snapDy_1 = _c.dy;
                if (snapDx_1 !== 0 || snapDy_1 !== 0) {
                    playAudio('PatternAdd');
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (!selectedShapeIds.includes((_a = item.data.id) !== null && _a !== void 0 ? _a : ''))
                                return item;
                            var c = item.data.center;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: c.x + snapDx_1, y: c.y + snapDy_1 } }) });
                        });
                    });
                    if (baseBoundsRef.current) {
                        baseBoundsRef.current = __assign(__assign({}, baseBoundsRef.current), { minX: baseBoundsRef.current.minX + snapDx_1, minY: baseBoundsRef.current.minY + snapDy_1, maxX: baseBoundsRef.current.maxX + snapDx_1, maxY: baseBoundsRef.current.maxY + snapDy_1, centerX: baseBoundsRef.current.centerX + snapDx_1, centerY: baseBoundsRef.current.centerY + snapDy_1 });
                    }
                    baseCirclesRef.current = baseCirclesRef.current.map(function (c) { return (__assign(__assign({}, c), { cx: c.cx + snapDx_1, cy: c.cy + snapDy_1 })); });
                    var snapRefShape = currentManipulative_1.find(function (m) { return m.data.id === selectedShapeIds[0]; });
                    var snapRefCenter = (_b = (_a = snapRefShape === null || snapRefShape === void 0 ? void 0 : snapRefShape.data) === null || _a === void 0 ? void 0 : _a.center) !== null && _b !== void 0 ? _b : { x: 0, y: 0 };
                    var snapFinalCenter_1 = { x: snapRefCenter.x + snapDx_1, y: snapRefCenter.y + snapDy_1 };
                    setAnnounceText('');
                    requestAnimationFrame(function () {
                        return setAnnounceText(t('text.patternShapeSnappedAt', {
                            name: t('text.patternBlockGroup'),
                            x: Math.round(snapFinalCenter_1.x),
                            y: Math.round(snapFinalCenter_1.y),
                        }));
                    });
                }
                var dragStateEntries = selectedShapeIds.flatMap(function (sid) {
                    var startCenter = shapeCentersAtDragStart.current[sid];
                    var current = currentManipulative_1.find(function (m) { return m.data.id === sid; });
                    if (!startCenter || !current)
                        return [];
                    var finalCenter = {
                        x: current.data.center.x + snapDx_1,
                        y: current.data.center.y + snapDy_1,
                    };
                    return [{ type: sid, id: sid.split('_')[1], propertyName: 'center', old: JSON.stringify(startCenter), new: JSON.stringify(finalCenter) }];
                });
                // Bring the whole selected cluster to front, preserving relative
                // order among its members. Functional update against live `prev`
                // (already includes the position/snap updates above) — never a
                // replacement built from a stale ref snapshot.
                var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var orderedIds_1 = manipulativeRef.current
                    .filter(function (m) { return selectedShapeIds.includes(m.data.id); })
                    .map(function (m) { return m.data.id; });
                var alreadyAtFront = orderedIds_1.length > 0 &&
                    beforeIds.slice(-orderedIds_1.length).join(',') === orderedIds_1.join(',');
                if (orderedIds_1.length > 0 && !alreadyAtFront) {
                    var afterIds = __spreadArray(__spreadArray([], beforeIds.filter(function (bid) { return !selectedShapeIds.includes(bid); }), true), orderedIds_1, true);
                    dragStateEntries.push({
                        type: 'multipleManipulatives',
                        id: orderedIds_1.join(','),
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds),
                        new: JSON.stringify(afterIds),
                    });
                    setManipulative(function (prev) { return bringManipulativesToFront(prev, orderedIds_1); });
                }
                if (dragStateEntries.length > 0 &&
                    !registerMultiSelectSaveEntries(groupId !== null && groupId !== void 0 ? groupId : selectedShapeIds[0], dragStateEntries)) {
                    saveState(dragStateEntries);
                }
                endMultiDrag();
            }
            dragStartRef.current = null;
            hasMoved.current = false;
            setIsDragging(false);
            document.body.style.cursor = '';
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
    };
    var onDragMouseDown = function (e) {
        e.preventDefault();
        e.stopPropagation();
        startGroupDrag(e.clientX, e.clientY);
    };
    var onDragTouchStart = function (e) {
        e.preventDefault();
        e.stopPropagation();
        startGroupDrag(e.touches[0].clientX, e.touches[0].clientY);
    };
    /* ================================================================ */
    /* GROUP ROTATE                                                      */
    /* ================================================================ */
    var startGroupRotate = function (clientX, clientY) {
        var b = baseBoundsRef.current;
        if (!b)
            return;
        rotatingRef.current = true;
        rotateHasMovedRef.current = false;
        var gcxC = b.centerX;
        var gcyC = b.centerY;
        groupCenterCanvasRef.current = { x: gcxC, y: gcyC };
        var canvasEl = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
        if (canvasEl) {
            var rect = canvasEl.getBoundingClientRect();
            groupCenterScreenRef.current = {
                x: rect.left + (gcxC * zoomLevel) - canvasEl.scrollLeft,
                y: rect.top + (gcyC * zoomLevel) - canvasEl.scrollTop,
            };
        }
        else {
            groupCenterScreenRef.current = { x: gcxC * zoomLevel, y: gcyC * zoomLevel };
        }
        var gcxS = groupCenterScreenRef.current.x;
        var gcyS = groupCenterScreenRef.current.y;
        rotateStartAngleRef.current = Math.atan2(clientY - gcyS, clientX - gcxS);
        initialCentersRef.current = {};
        initialRotationsRef.current = {};
        initialScalesRef.current = {};
        selectedShapes.forEach(function (item) {
            var _a, _b, _c;
            var iid = (_a = item.data.id) !== null && _a !== void 0 ? _a : '';
            initialCentersRef.current[iid] = { x: item.data.center.x, y: item.data.center.y };
            initialRotationsRef.current[iid] = (_b = item.data.rotation) !== null && _b !== void 0 ? _b : 0;
            initialScalesRef.current[iid] = (_c = item.data.scale) !== null && _c !== void 0 ? _c : 1;
        });
        document.body.style.cursor = 'grabbing';
        var onMove = function (e) {
            if (!rotatingRef.current)
                return;
            var cx = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var cy = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var curAngle = Math.atan2(cy - gcyS, cx - gcxS);
            var delta = curAngle - rotateStartAngleRef.current;
            var degDelta = (delta * 180) / Math.PI;
            if (Math.abs(degDelta) > 1) {
                rotateHasMovedRef.current = true;
            }
            rotDeltaRef.current = degDelta;
            setRotDelta(degDelta);
            var cos = Math.cos(delta);
            var sin = Math.sin(delta);
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a, _b;
                    var iid = (_a = item.data.id) !== null && _a !== void 0 ? _a : '';
                    var initTL = initialCentersRef.current[iid];
                    if (!initTL)
                        return item;
                    var initRot = (_b = initialRotationsRef.current[iid]) !== null && _b !== void 0 ? _b : 0;
                    var st = item.data.shapeType;
                    var sb = SHAPE_BOUNDS[st] || { x: 0, y: 0, w: UNIT, h: UNIT };
                    var halfW = sb.w / 2;
                    var halfH = sb.h / 2;
                    var shapeCx = initTL.x + halfW;
                    var shapeCy = initTL.y + halfH;
                    var dx = shapeCx - gcxC;
                    var dy = shapeCy - gcyC;
                    var newCx = gcxC + dx * cos - dy * sin;
                    var newCy = gcyC + dx * sin + dy * cos;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newCx - halfW, y: newCy - halfH }, rotation: initRot + degDelta }) });
                });
            });
        };
        var onUp = function () {
            if (!rotatingRef.current)
                return;
            document.body.style.cursor = '';
            var finalDelta = rotDeltaRef.current;
            setRotDelta(0);
            rotDeltaRef.current = 0;
            requestAnimationFrame(function () { rotatingRef.current = false; });
            var b = baseBoundsRef.current;
            var gcx = b ? b.centerX : 0;
            var gcy = b ? b.centerY : 0;
            // Snap rotation angle to nearest 30° if within ±8° (mirrors standalone handleHandleRotateEnd)
            var ANGLE_SNAP_DEG = 30;
            var ANGLE_SNAP_THRESHOLD = 8;
            var rawGroupAngle = groupAngleRef.current + finalDelta;
            var nearestSnap = Math.round(rawGroupAngle / ANGLE_SNAP_DEG) * ANGLE_SNAP_DEG;
            var angleDiff = nearestSnap - rawGroupAngle;
            var angleCorrectionDeg = Math.abs(angleDiff) <= ANGLE_SNAP_THRESHOLD ? angleDiff : 0;
            var totalDelta = finalDelta + angleCorrectionDeg;
            // Build corrected shape positions (apply angle correction around group center)
            var currentManipulative = manipulativeRef.current;
            var correctionRad = (angleCorrectionDeg * Math.PI) / 180;
            var cosC = Math.cos(correctionRad);
            var sinC = Math.sin(correctionRad);
            var correctedShapes = selectedShapeIds.flatMap(function (sid) {
                var _a, _b;
                var item = currentManipulative.find(function (m) { return m.data.id === sid; });
                if (!item)
                    return [];
                var d = item.data;
                var st = d.shapeType;
                var sb = SHAPE_BOUNDS[st] || { x: 0, y: 0, w: UNIT, h: UNIT };
                var halfW = sb.w / 2;
                var halfH = sb.h / 2;
                var cx = d.center.x + halfW;
                var cy = d.center.y + halfH;
                var dx2 = cx - gcx;
                var dy2 = cy - gcy;
                var newCx = gcx + dx2 * cosC - dy2 * sinC;
                var newCy = gcy + dx2 * sinC + dy2 * cosC;
                return [{
                        id: sid,
                        shapeType: d.shapeType,
                        center: { x: newCx - halfW, y: newCy - halfH },
                        rotation: ((_a = d.rotation) !== null && _a !== void 0 ? _a : 0) + angleCorrectionDeg,
                        scale: (_b = d.scale) !== null && _b !== void 0 ? _b : 1,
                    }];
            });
            // Apply angle correction if needed
            if (angleCorrectionDeg !== 0) {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        var corrected = correctedShapes.find(function (s) { var _a; return s.id === ((_a = item.data.id) !== null && _a !== void 0 ? _a : ''); });
                        if (!corrected)
                            return item;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: corrected.center, rotation: corrected.rotation }) });
                    });
                });
                if (b) {
                    baseBoundsRef.current = __assign(__assign({}, b), { centerX: gcx, centerY: gcy });
                }
            }
            // Position snap using corrected positions
            var snapShapes = correctedShapes.length > 0
                ? correctedShapes
                : selectedShapeIds.flatMap(function (sid) {
                    var _a, _b;
                    var item = currentManipulative.find(function (m) { return m.data.id === sid; });
                    if (!item)
                        return [];
                    var d = item.data;
                    return [{ id: sid, shapeType: d.shapeType, center: d.center, rotation: (_a = d.rotation) !== null && _a !== void 0 ? _a : 0, scale: (_b = d.scale) !== null && _b !== void 0 ? _b : 1 }];
                });
            var rotExcludeIds = groupId ? __spreadArray(__spreadArray([], selectedShapeIds, true), [groupId], false) : selectedShapeIds;
            var _a = computeGroupSnapDelta(snapShapes, rotExcludeIds, currentManipulative, playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none'), snapDx = _a.dx, snapDy = _a.dy;
            if (angleCorrectionDeg !== 0 || snapDx !== 0 || snapDy !== 0)
                playAudio('PatternAdd');
            if (snapDx !== 0 || snapDy !== 0) {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        var _a;
                        if (!selectedShapeIds.includes((_a = item.data.id) !== null && _a !== void 0 ? _a : ''))
                            return item;
                        var c = item.data.center;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: c.x + snapDx, y: c.y + snapDy } }) });
                    });
                });
            }
            baseBoundsRef.current = null;
            baseCirclesRef.current = [];
            setGroupAngle(function (prev) { return prev + totalDelta; });
            selectedShapeIds.forEach(function (sid) {
                var initCenter = initialCentersRef.current[sid];
                var initRot = initialRotationsRef.current[sid];
                var corrected = correctedShapes.find(function (s) { return s.id === sid; });
                var finalCenter = corrected
                    ? { x: corrected.center.x + snapDx, y: corrected.center.y + snapDy }
                    : (function () {
                        var cur = currentManipulative.find(function (m) { return m.data.id === sid; });
                        return cur ? { x: cur.data.center.x + snapDx, y: cur.data.center.y + snapDy } : initCenter;
                    })();
                var finalRot = corrected ? corrected.rotation : (function () {
                    var cur = currentManipulative.find(function (m) { return m.data.id === sid; });
                    return cur ? cur.data.rotation : (initRot !== null && initRot !== void 0 ? initRot : 0);
                })();
                saveState({
                    type: sid, id: sid.split('_')[1], propertyName: 'completeData',
                    old: JSON.stringify({ center: initCenter, rotation: initRot }),
                    new: JSON.stringify({ center: finalCenter, rotation: finalRot }),
                });
            });
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
    };
    var onRotateMouseDown = function (e) {
        e.preventDefault();
        e.stopPropagation();
        startGroupRotate(e.clientX, e.clientY);
    };
    var onRotateTouchStart = function (e) {
        e.preventDefault();
        e.stopPropagation();
        startGroupRotate(e.touches[0].clientX, e.touches[0].clientY);
    };
    /* ================================================================ */
    /* RENDER                                                            */
    /* ================================================================ */
    var isLocked = selectedShapes.length > 0 && selectedShapes.every(function (m) { var _a; return ((_a = m.data.lock) === null || _a === void 0 ? void 0 : _a.positioning) === true; });
    var groupNumber = useMemo(function () {
        if (!groupId)
            return 1;
        var seenGroups = [];
        manipulative.forEach(function (m) {
            var _a;
            var gId = m.data.groupId;
            if (gId && ((_a = m.data.id) === null || _a === void 0 ? void 0 : _a.startsWith('patternShapes_')) && !seenGroups.includes(gId)) {
                seenGroups.push(gId);
            }
        });
        return seenGroups.indexOf(groupId) + 1 || 1;
    }, [manipulative, groupId]);
    var shapeDescriptions = selectedShapes.map(function (item) {
        var _a;
        var d = item.data;
        var st = d.shapeType;
        var color = String((_a = d.color) !== null && _a !== void 0 ? _a : '');
        var peers = manipulative.filter(function (m) { var _a, _b; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'patternShapes' && m.data.shapeType === st && String((_b = m.data.color) !== null && _b !== void 0 ? _b : '') === color; });
        var num = peers.findIndex(function (m) { return m.data.id === d.id; }) + 1;
        return t('text.patternPickerShapeTarget', {
            shapeType: t("text.".concat(st), { defaultValue: st }),
            color: t("text.color_".concat(color), { defaultValue: color }),
            number: num,
        });
    });
    var groupAriaLabel = t('text.patternBlockGroupLabel', {
        count: selectedShapes.length,
        number: groupNumber,
        shapes: shapeDescriptions.join(', '),
    });
    var onGroupKeyDown = function (e) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if (e.key === 'Escape') {
            e.preventDefault();
            setSelectedTool([]);
            return;
        }
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.preventDefault();
            if (isLocked)
                return;
            var step = e.shiftKey ? 50 : 10;
            var scaledStep = step / zoomLevel;
            var deltas = {
                ArrowUp: { x: 0, y: -scaledStep },
                ArrowDown: { x: 0, y: scaledStep },
                ArrowLeft: { x: -scaledStep, y: 0 },
                ArrowRight: { x: scaledStep, y: 0 },
            };
            var d_1 = deltas[e.key];
            var startCenters_1 = {};
            if (!keyMoveStartRef.current) {
                selectedShapes.forEach(function (item) {
                    var _a;
                    startCenters_1[(_a = item.data.id) !== null && _a !== void 0 ? _a : ''] = __assign({}, item.data.center);
                });
                keyMoveStartRef.current = {
                    x: (_d = (_c = (_b = (_a = selectedShapes[0]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.center) === null || _c === void 0 ? void 0 : _c.x) !== null && _d !== void 0 ? _d : 0,
                    y: (_h = (_g = (_f = (_e = selectedShapes[0]) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.center) === null || _g === void 0 ? void 0 : _g.y) !== null && _h !== void 0 ? _h : 0,
                };
                keyMoveStartRef._centers = startCenters_1;
            }
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a;
                    if (!selectedShapeIds.includes((_a = item.data.id) !== null && _a !== void 0 ? _a : ''))
                        return item;
                    var c = item.data.center;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: c.x + d_1.x, y: c.y + d_1.y } }) });
                });
            });
            if (baseBoundsRef.current) {
                baseBoundsRef.current = __assign(__assign({}, baseBoundsRef.current), { minX: baseBoundsRef.current.minX + d_1.x, minY: baseBoundsRef.current.minY + d_1.y, maxX: baseBoundsRef.current.maxX + d_1.x, maxY: baseBoundsRef.current.maxY + d_1.y, centerX: baseBoundsRef.current.centerX + d_1.x, centerY: baseBoundsRef.current.centerY + d_1.y });
            }
            baseCirclesRef.current = baseCirclesRef.current.map(function (c) { return (__assign(__assign({}, c), { cx: c.cx + d_1.x, cy: c.cy + d_1.y })); });
            if (keyMoveSaveTimer.current)
                clearTimeout(keyMoveSaveTimer.current);
            keyMoveSaveTimer.current = window.setTimeout(function () {
                var _a, _b, _c, _d;
                var latestManipulative = manipulativeRef.current;
                var savedCenters = (_a = keyMoveStartRef._centers) !== null && _a !== void 0 ? _a : {};
                // Snap on settle — same as mouse drag onUp
                var snapShapes = selectedShapeIds.flatMap(function (sid) {
                    var _a, _b;
                    var item = latestManipulative.find(function (m) { return m.data.id === sid; });
                    if (!item)
                        return [];
                    var sd = item.data;
                    return [{ id: sid, shapeType: sd.shapeType, center: sd.center, rotation: (_a = sd.rotation) !== null && _a !== void 0 ? _a : 0, scale: (_b = sd.scale) !== null && _b !== void 0 ? _b : 1 }];
                });
                var excludeIdsKb = groupId ? __spreadArray(__spreadArray([], selectedShapeIds, true), [groupId], false) : selectedShapeIds;
                var _e = computeGroupSnapDelta(snapShapes, excludeIdsKb, latestManipulative, playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none'), snapDx = _e.dx, snapDy = _e.dy;
                if (snapDx !== 0 || snapDy !== 0) {
                    playAudio('PatternAdd');
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (!selectedShapeIds.includes((_a = item.data.id) !== null && _a !== void 0 ? _a : ''))
                                return item;
                            var c = item.data.center;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: c.x + snapDx, y: c.y + snapDy } }) });
                        });
                    });
                    if (baseBoundsRef.current) {
                        baseBoundsRef.current = __assign(__assign({}, baseBoundsRef.current), { minX: baseBoundsRef.current.minX + snapDx, minY: baseBoundsRef.current.minY + snapDy, maxX: baseBoundsRef.current.maxX + snapDx, maxY: baseBoundsRef.current.maxY + snapDy, centerX: baseBoundsRef.current.centerX + snapDx, centerY: baseBoundsRef.current.centerY + snapDy });
                    }
                    baseCirclesRef.current = baseCirclesRef.current.map(function (c) { return (__assign(__assign({}, c), { cx: c.cx + snapDx, cy: c.cy + snapDy })); });
                }
                // Batch all shape saves into one undo entry so a single Ctrl+Z undoes the whole move
                var stateEntries = selectedShapeIds.flatMap(function (sid) {
                    var startC = savedCenters[sid];
                    var current = latestManipulative.find(function (m) { return m.data.id === sid; });
                    if (!startC || !current)
                        return [];
                    var finalCenter = {
                        x: current.data.center.x + snapDx,
                        y: current.data.center.y + snapDy,
                    };
                    return [{ type: sid, id: sid.split('_')[1], propertyName: 'center', old: JSON.stringify(startC), new: JSON.stringify(finalCenter) }];
                });
                if (stateEntries.length > 0)
                    saveState(stateEntries);
                var refShape = latestManipulative.find(function (m) { return m.data.id === selectedShapeIds[0]; });
                var preSnapCenter = (_c = (_b = refShape === null || refShape === void 0 ? void 0 : refShape.data) === null || _b === void 0 ? void 0 : _b.center) !== null && _c !== void 0 ? _c : { x: 0, y: 0 };
                var newCenter = { x: preSnapCenter.x + snapDx, y: preSnapCenter.y + snapDy };
                var startC0 = (_d = savedCenters[selectedShapeIds[0]]) !== null && _d !== void 0 ? _d : newCenter;
                setAnnounceText('');
                if (snapDx !== 0 || snapDy !== 0) {
                    requestAnimationFrame(function () {
                        return setAnnounceText(t('text.patternShapeSnappedAt', {
                            name: t('text.patternBlockGroup'),
                            x: Math.round(newCenter.x),
                            y: Math.round(newCenter.y),
                        }));
                    });
                }
                else {
                    var dirs_1 = [];
                    var dx = Math.round(newCenter.x - startC0.x);
                    var dy = Math.round(newCenter.y - startC0.y);
                    if (dy < 0)
                        dirs_1.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                    if (dy > 0)
                        dirs_1.push("".concat(t('text.down'), " ").concat(dy));
                    if (dx < 0)
                        dirs_1.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                    if (dx > 0)
                        dirs_1.push("".concat(t('text.right'), " ").concat(dx));
                    requestAnimationFrame(function () {
                        return setAnnounceText(t('text.manipulativeMoved', {
                            name: t('text.patternBlockGroup'),
                            direction: dirs_1.join(" ".concat(t('text.and'), " ")),
                            x: Math.round(newCenter.x),
                            y: Math.round(newCenter.y),
                        }));
                    });
                }
                keyMoveStartRef.current = null;
            }, 500);
            return;
        }
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            if (groupId && !selectedTool.includes(groupId)) {
                setSelectedTool([groupId]);
            }
            var rect = (_j = dragAreaRef.current) === null || _j === void 0 ? void 0 : _j.getBoundingClientRect();
            if (rect) {
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: groupId !== null && groupId !== void 0 ? groupId : selectedShapeIds[0] });
            }
        }
    };
    var renderBounds = baseBoundsRef.current || liveBounds;
    // For permanent groups (always in DOM): render just the children container when not active or no bounds yet
    if (!renderBounds || selectedShapes.length < 2) {
        if (!groupId)
            return null;
        return (_jsx("div", { style: { position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }, children: children }));
    }
    var renderCircles = baseCirclesRef.current.length > 0 ? baseCirclesRef.current : liveCircles;
    var totalAngle = groupAngle + rotDelta;
    var lineHeight = HANDLE_GAP + HANDLE_SIZE / 2;
    var n = renderCircles.length;
    var centroidX = n > 0 ? renderCircles.reduce(function (sum, c) { return sum + c.cx; }, 0) / n : renderBounds.centerX;
    var centroidY = n > 0 ? renderCircles.reduce(function (sum, c) { return sum + c.cy; }, 0) / n : renderBounds.centerY;
    return (_jsxs("div", { style: {
            position: 'absolute',
            left: 0, top: 0,
            width: '100%', height: '100%',
            pointerEvents: 'none',
        }, children: [children, _jsxs("div", { style: {
                    position: 'absolute',
                    left: 0, top: 0,
                    width: '100%', height: '100%',
                    pointerEvents: 'none',
                    transform: "rotate(".concat(totalAngle, "deg)"),
                    transformOrigin: "".concat(renderBounds.centerX, "px ").concat(renderBounds.centerY, "px"),
                    zIndex: 2,
                    // Contains the handle sub-elements' z-indices (2-5) to just this
                    // group's own children instead of leaking out to compete with other
                    // manipulatives.
                    isolation: 'isolate',
                }, children: [_jsx("div", { ref: dragAreaRef, className: "psSelectionGroup psSelectionGroup__dragArea", "data-manipulative-id": groupId !== null && groupId !== void 0 ? groupId : selectedShapeIds[0], onMouseDown: isGroupActive && !isLocked ? onDragMouseDown : undefined, onTouchStart: isGroupActive && !isLocked ? onDragTouchStart : undefined, onContextMenu: function (e) {
                            var _a;
                            e.preventDefault();
                            if (groupId && !selectedTool.includes(groupId))
                                setSelectedTool([groupId]);
                            var rect = (_a = dragAreaRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                            if (rect)
                                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: groupId !== null && groupId !== void 0 ? groupId : selectedShapeIds[0] });
                        }, role: "group", tabIndex: 0, "aria-label": duplicateOverrideLabel !== null && duplicateOverrideLabel !== void 0 ? duplicateOverrideLabel : groupAriaLabel, onFocus: function (e) {
                            var _a, _b;
                            try {
                                lastFocusWasScreenReader.current = !!((_b = (_a = e.target).matches) === null || _b === void 0 ? void 0 : _b.call(_a, ':focus-visible'));
                            }
                            catch (_c) {
                                lastFocusWasScreenReader.current = false;
                            }
                            if (groupId && !selectedTool.includes(groupId))
                                setSelectedTool([groupId]);
                        }, onBlur: function () { lastFocusWasScreenReader.current = false; }, onClick: function (e) {
                            var _a;
                            // iPad VoiceOver: double-tap synthesizes a click with :focus-visible set beforehand
                            if (IS_TOUCH_DEVICE && lastFocusWasScreenReader.current) {
                                e.preventDefault();
                                lastFocusWasScreenReader.current = false;
                                if (groupId && !selectedTool.includes(groupId))
                                    setSelectedTool([groupId]);
                                var rect = (_a = dragAreaRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                                if (rect)
                                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: groupId !== null && groupId !== void 0 ? groupId : selectedShapeIds[0] });
                            }
                        }, onKeyDown: onGroupKeyDown, style: {
                            position: 'absolute',
                            left: renderBounds.minX,
                            top: renderBounds.minY,
                            width: renderBounds.width,
                            height: renderBounds.height,
                            cursor: isGroupActive && !isLocked ? (isDragging ? 'grabbing' : 'grab') : 'default',
                            // When the group is not active (not selected), pass mouse events through so
                            // clicks don't focus this drag area and accidentally fire setSelectedTool([groupId]).
                            // Tab-key focus still works regardless of pointer-events.
                            pointerEvents: isGroupActive ? 'auto' : 'none',
                            background: 'transparent',
                            zIndex: 3,
                        } }), _jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: {
                            position: 'absolute', width: '1px', height: '1px',
                            padding: 0, margin: '-1px', overflow: 'hidden',
                            clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
                        }, children: announceText }), (isGroupActive && !isLocked || rotateProxyFocusedRef.current) && (_jsx("button", { ref: rotateProxyRef, className: "psSelectionGroup__rotateHandle", "aria-label": t('text.rotate'), role: "slider", "aria-valuemin": -1, "aria-valuemax": 361, "aria-valuenow": Math.round(((groupAngle % 360) + 360) % 360), "aria-hidden": (!isGroupActive || isLocked) && !rotateProxyFocusedRef.current ? true : undefined, tabIndex: isGroupActive && !isLocked ? 0 : -1, onFocus: function () { rotateProxyFocusedRef.current = true; }, onBlur: function () { rotateProxyFocusedRef.current = false; }, onKeyDown: function (e) {
                            var _a;
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                (_a = dragAreaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                return;
                            }
                            if (!['ArrowRight', 'ArrowUp', 'ArrowLeft', 'ArrowDown'].includes(e.key))
                                return;
                            e.preventDefault();
                            e.stopPropagation();
                            var isClockwise = e.key === 'ArrowRight' || e.key === 'ArrowUp';
                            var step = e.shiftKey ? 15 : 1;
                            rotateGroup(isClockwise ? step : -step);
                        }, style: {
                            position: 'absolute',
                            left: renderBounds.centerX - HANDLE_SIZE / 2,
                            top: renderBounds.minY - lineHeight - HANDLE_SIZE / 2,
                            width: HANDLE_SIZE,
                            height: HANDLE_SIZE,
                            pointerEvents: 'none',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            zIndex: 5,
                        } })), isGroupActive && (_jsx("div", { className: "psSelectionGroup__boundingBox", style: {
                            position: 'absolute',
                            left: renderBounds.minX, top: renderBounds.minY,
                            width: renderBounds.width, height: renderBounds.height,
                            border: '1.5px solid #333', borderRadius: 2,
                            pointerEvents: 'none',
                        } })), isGroupActive && (_jsxs("svg", { "aria-hidden": "true", style: {
                            position: 'absolute',
                            left: 0, top: 0,
                            width: '100%', height: '100%',
                            pointerEvents: 'none',
                            overflow: 'visible',
                            zIndex: 2,
                        }, children: [!isLocked && _jsx("line", { x1: centroidX, y1: centroidY, x2: renderBounds.centerX, y2: renderBounds.minY - lineHeight, stroke: "#bbb", strokeWidth: 1.5 }), renderCircles.map(function (circle) { return (_jsx("line", { x1: centroidX, y1: centroidY, x2: circle.cx, y2: circle.cy, stroke: "#bbb", strokeWidth: 1.5 }, "spoke-".concat(circle.id))); }), n >= 2 && (_jsx("circle", { cx: centroidX, cy: centroidY, r: CIRCLE_SIZE / 2 + 2, fill: "#fff", stroke: "#293273", strokeWidth: 2.5 }))] })), isGroupActive && !isLocked && _jsx("button", { ref: rotateHandleVisualRef, className: "psSelectionGroup psSelectionGroup__rotateHandle", "data-manipulative-id": groupId !== null && groupId !== void 0 ? groupId : selectedShapeIds[0], tabIndex: -1, "aria-hidden": "true", onMouseDown: onRotateMouseDown, onTouchStart: onRotateTouchStart, onMouseEnter: function () { return setRotateHandleHover(true); }, onMouseLeave: function () { return setRotateHandleHover(false); }, style: {
                            position: 'absolute',
                            left: renderBounds.centerX - HANDLE_SIZE / 2,
                            top: renderBounds.minY - lineHeight - HANDLE_SIZE / 2,
                            width: HANDLE_SIZE, height: HANDLE_SIZE,
                            cursor: 'grab', pointerEvents: 'auto', zIndex: 4,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            touchAction: 'none', userSelect: 'none',
                            border: 'none', background: 'none', padding: 0,
                        }, children: _jsx("img", { src: rotateHandleHover
                                ? 'assets/images/manipulative/patternShape/Rotate_H.svg'
                                : 'assets/images/manipulative/patternShape/Rotate_N.svg', alt: "", width: HANDLE_SIZE, height: HANDLE_SIZE, style: { width: '100%', height: '100%', objectFit: 'contain' }, draggable: false }) }), isGroupActive && renderCircles.map(function (circle) { return (_jsx("div", { className: "psSelectionGroup psSelectionGroup__dragCircle", "data-manipulative-id": groupId !== null && groupId !== void 0 ? groupId : selectedShapeIds[0], onMouseDown: isLocked ? undefined : onDragMouseDown, onTouchStart: isLocked ? undefined : onDragTouchStart, style: {
                            position: 'absolute',
                            left: circle.cx - CIRCLE_SIZE / 2,
                            top: circle.cy - CIRCLE_SIZE / 2,
                            width: CIRCLE_SIZE, height: CIRCLE_SIZE,
                            borderRadius: '50%', background: '#fff',
                            border: '2px solid #555',
                            cursor: isLocked ? 'default' : isDragging ? 'grabbing' : 'grab',
                            pointerEvents: 'auto', zIndex: 3,
                        } }, circle.id)); })] })] }));
};
export default PatternShapesSelectionGroup;
